import { FC } from 'react';
import styled from 'styled-components';
import { getCountryFlag } from 'i18n/config/countryFlags';
import { getCountryName } from 'i18n/countries';
import localeFlags from 'i18n/config/localeFlags';
import { useLocale } from 'i18n';

interface IProps {
  /** ISO 3166-1 alpha-2, or null when the student has not stated a nationality. */
  nationality?: string | null;
  /** The student's support language code, used only when there is no nationality. */
  supportLanguageCode?: string | null;
  className?: string;
}

/**
 * The flag that stands for a student.
 *
 * Support language used to fill this slot everywhere, which reads as a nationality but is
 * not one — a Ukrainian studying through Russian showed a Russian flag. A stated
 * nationality wins; without one the support-language flag stays, so nothing changes for
 * the students who have not answered.
 */
const StudentFlag: FC<IProps> = ({ nationality, supportLanguageCode, className }) => {
  const { localeCode } = useLocale();

  // Every entry in localeFlags carries a flag, so a miss there means the code is not a
  // language the app knows — not that a known language happens to have no image.
  const language = supportLanguageCode ? localeFlags.get(supportLanguageCode) : undefined;

  const flag = nationality
    ? { src: getCountryFlag(nationality), name: getCountryName(nationality, localeCode) }
    : language && { src: language.flag, name: language.nativeName };

  if (!flag) return null;

  return (
    <SFlag
      className={className}
      src={flag.src}
      alt={flag.name ?? ''}
      title={flag.name ?? undefined}
    />
  );
};

export default StudentFlag;

const SFlag = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;
